import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { fError } from '@/utils/format-error'
import axiosInstance, { endpoints } from '@/lib/axios'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'

import type { Trend, TrendFilters, TrendListResponse } from '../types'

import TrendsTable from '../trends-table'
import TrendsFilter from '../trends-filter'
import TrendDetailDialog from '../trend-detail-dialog'

// ----------------------------------------------------------------------

const PAGE_SIZE = 10

export default function TrendsView() {
  const [trends, setTrends] = useState<Trend[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [filters, setFilters] = useState<TrendFilters>({
    page: 1,
    limit: PAGE_SIZE,
    status: '',
    deleted: 'false',
    startDate: '',
    endDate: '',
    orderBy: 'createdAt:desc',
  })
  const [selectedTrend, setSelectedTrend] = useState<Trend | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    trend: Trend | null
  }>({ open: false, trend: null })
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)

  const fetchTrends = async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number | undefined> = {
        page: filters.page,
        limit: filters.limit,
        orderBy: filters.orderBy,
      }

      if (filters.status) params.status = filters.status
      params.deleted = filters.deleted
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate

      const { data } = await axiosInstance.get<TrendListResponse>(
        endpoints.trends.list,
        { params },
      )
      setTrends(data.data)
      setTotal(data.total)
      setSelectedIds((prev) =>
        prev.filter((id) => data.data.some((trend) => trend.id === id)),
      )
    } catch (error) {
      toast.error(fError(error))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTrends()
  }, [filters])

  const handleView = async (trend: Trend) => {
    setSelectedTrend(trend)
    setDetailOpen(true)

    try {
      const { data } = await axiosInstance.get<Trend>(
        endpoints.trends.detail(trend.id),
      )
      setSelectedTrend(data)
    } catch (error) {
      toast.error(fError(error))
    }
  }

  const handleDelete = (trend: Trend) => {
    setConfirmDialog({ open: true, trend })
  }

  const handleTogglePublic = async (trend: Trend, isPublic: boolean) => {
    const nextPrivacy = isPublic ? 'public' : 'private'
    const previousTrends = trends

    setTrends((prev) =>
      prev.map((item) =>
        item.id === trend.id ? { ...item, privacy: nextPrivacy } : item,
      ),
    )

    try {
      const { data } = await axiosInstance.put<Trend>(
        endpoints.trends.updateVisibility(trend.id),
        { isPublic },
      )
      setTrends((prev) =>
        prev.map((item) => (item.id === trend.id ? data : item)),
      )
      if (selectedTrend?.id === trend.id) {
        setSelectedTrend(data)
      }
      toast.success(isPublic ? 'ตั้งเป็นสาธารณะแล้ว' : 'ตั้งเป็นส่วนตัวแล้ว')
    } catch (error) {
      setTrends(previousTrends)
      toast.error(fError(error))
    }
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog.trend || deleteLoading) return

    setDeleteLoading(true)
    try {
      await axiosInstance.delete(endpoints.trends.delete(confirmDialog.trend.id))
      toast.success('ลบคลิปเทรนด์ถาวรแล้ว')
      setConfirmDialog({ open: false, trend: null })
      fetchTrends()
    } catch (error) {
      toast.error(fError(error))
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return
    setBulkConfirmOpen(true)
  }

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0 || deleteLoading) return

    setDeleteLoading(true)
    try {
      await Promise.all(
        selectedIds.map((id) => axiosInstance.delete(endpoints.trends.delete(id))),
      )
      toast.success(`ลบคลิปเทรนด์ถาวร ${selectedIds.length} รายการแล้ว`)
      setBulkConfirmOpen(false)
      setSelectedIds([])
      fetchTrends()
    } catch (error) {
      toast.error(fError(error))
    } finally {
      setDeleteLoading(false)
    }
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  return (
    <div className="w-full space-y-6">
      <TrendsFilter
        filters={filters}
        total={total}
        selectedCount={selectedIds.length}
        onFiltersChange={setFilters}
        onBulkDelete={handleBulkDelete}
      />

      <TrendsTable
        trends={trends}
        loading={loading}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        onView={handleView}
        onDelete={handleDelete}
        onTogglePublic={handleTogglePublic}
      />

      {Math.ceil(total / PAGE_SIZE) > 1 && (
        <CustomPagination
          currentPage={filters.page}
          totalPages={Math.ceil(total / PAGE_SIZE)}
          onPageChange={handlePageChange}
        />
      )}

      <TrendDetailDialog
        trend={selectedTrend}
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open)
          if (!open) setSelectedTrend(null)
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, trend: null })}
        title="ยืนยันการลบ"
        description={`คุณต้องการลบคลิปเทรนด์ "${confirmDialog.trend?.caption || confirmDialog.trend?.id}" ถาวรหรือไม่? การดำเนินการนี้จะลบข้อมูลออกจากระบบ`}
        confirmText="ลบถาวร"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        onOpenChange={setBulkConfirmOpen}
        title="ยืนยันการลบหลายรายการ"
        description={`คุณต้องการลบคลิปเทรนด์ที่เลือก ${selectedIds.length} รายการถาวรหรือไม่? การดำเนินการนี้จะลบข้อมูลออกจากระบบ`}
        confirmText="ลบถาวร"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmBulkDelete}
        loading={deleteLoading}
      />
    </div>
  )
}
